import { format as d3format } from "d3-format";

/**
 * Formats a numeric value to a fixed number of decimal places.
 *
 * Accepts numbers or numeric strings and returns a formatted string with the specified precision.
 * If the value is invalid or not a number, it returns a fallback string (default: "-").
 *
 * @param value - The value to format. Can be a number or a string that represents a number.
 * @param precision - The number of digits to appear after the decimal point (default is 0).
 * @param fallback - The string to return if the value is not a valid number (default is "-").
 * @returns A string representing the formatted number or the fallback.
 *
 * @example
 * formatNumber(123.4567, 2);       // "123.46"
 * formatNumber("99.9", 1);         // "99.9"
 * formatNumber("abc", 2);          // "-"
 * formatNumber(undefined, 3);      // "-"
 */
export function formatNumber(value: unknown, precision = 0, fallback = "-"): string {
	if (typeof value === "number") {
		return value.toFixed(precision);
	}
	if (typeof value === "string" && !isNaN(Number(value))) {
		return Number(value).toFixed(precision);
	}
	return fallback;
}

export function autoFormat(value: number): string {
	const abs = Math.abs(value);

	if (abs === 0) return "0";

	if (abs < 0.001 || abs >= 1e6) {
		return d3format(".2e")(value);
	}

	if (abs >= 1e3) {
		return d3format(".2s")(value);
	}

	return d3format(".2f")(value);
}

export function formatAdaptive(value: number, undefinedValue = "-"): string {
	if (value === 0) return "0";
	if (!value) {
		return undefinedValue;
	}

	const abs = Math.abs(value);

	if (abs < 0.000001) return d3format(".8f")(value);
	if (abs < 0.01) return d3format(".6f")(value);
	if (abs < 1) return d3format(".4f")(value);
	if (abs < 1000) return d3format(".0f")(value);
	return d3format(",.0f")(value);
}
